import {MouseEvent} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {mt} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {multiToSingleLineString, numberFormat, pluralize, priceFormat, priceM2Format, SUP2} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ResponsiveTable} from "../../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../../atoms/ResponsiveTableRow";
import {flatsPluralize} from "../../../real_estate/constants/flats_pluralize";
import {getCurrency} from "../../../utils/get_currency";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {DetailSectionWrapper} from "../../atoms/DetailSectionWrapper";
import {getOfferTypeNamePlural, getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {PropertyFlatType} from "../../types/PropertyFlatType";
import {PropertyHouseType} from "../../types/PropertyHouseType";
import {DetailViewHeader} from "./DetailViewHeader";
import {OfferDetailFundsProtection} from "./OfferDetailFundsProtection";

interface IProps {
    changeVisibility: (e: MouseEvent) => void;
    showPrice: boolean;
    offer: IOfferDetail;
    variables: {
        offer_funds_protection: IValueLabel[];
    } | null;
}

const detachedHouse = pluralize(["wolnostojący", "wolnostojące", "wolnostojących"]);
const semiDetachedHouse = pluralize(["bliźniak", "bliźniaki", "bliźniaków"]);
const serialHouse = pluralize(["szeregowy", "szeregowe", "szeregowych"]);

function getPropertyHouseType(propertyHouseType: PropertyHouseType, quantity: number) {
    switch (propertyHouseType) {
        case PropertyHouseType.DETACHED:
            return detachedHouse(quantity);
        case PropertyHouseType.SEMI_DETACHED:
            return semiDetachedHouse(quantity);
        case PropertyHouseType.SERIAL:
            return serialHouse(quantity);
        case PropertyHouseType.WOODEN:
            return "z bali";
        default:
            throw new Error("Unknown property house type");
    }
}

function getPropertyFlatType(propertyFlatType: PropertyFlatType) {
    switch (propertyFlatType) {
        case PropertyFlatType.STANDARD:
            return "Standard";
        case PropertyFlatType.PENTHOUSE:
            return "Penthouse";
        case PropertyFlatType.APARTMENT:
            return "Apartament";
        default:
            throw new Error("Unknown property flat type");
    }
}

export const OfferDetailCurrentInfo = (props: IProps) => {
    const {offer, changeVisibility, showPrice, variables} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;

    const currency = getCurrency({currency: offer.currency});

    const localPlural = pluralize(["lokal", "lokale", "lokali"]);
    const propertiesAvailable = pluralize(["dostępny jest", "dostępne są", "dostępnych jest"]);
    const housesAvailable = pluralize(["dostępny jest", "dostępne są", "dostępnych jest"]);
    const apartmentsLeftPlural = pluralize(["apartament", "apartamenty", "apartamentów"]);
    const housePlural = pluralize(["dom", "domy", "domów"]);
    const commercialsPlural = pluralize(["lokal użytkowy", "lokale użytkowe", "lokali użytkowych"]);
    const offersPlural = pluralize(["oferta rekomendowana", "oferty rekomendowane", "ofert rekomendowanych"]);
    const isNearbyPluralize = pluralize(["znajduje się", "znajdują się", "znajduje się"]);

    const renderFlatTypes = () => {
        return Object.entries(offer.stats?.flat_types || {}).map(([type, quantity]) => {
            const intType = parseInt(type, 10);
            if (intType === PropertyFlatType.STANDARD) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            } else if (intType === PropertyFlatType.PENTHOUSE) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            } else if (intType === PropertyFlatType.APARTMENT) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            }
        });
    };

    const renderHouseTypes = () => {
        const houseTypes = [PropertyHouseType.DETACHED, PropertyHouseType.SEMI_DETACHED, PropertyHouseType.SERIAL, PropertyHouseType.WOODEN];

        return Object.entries(offer.stats?.house_types || {}).map(([type, quantity]) => {
            const intType = parseInt(type, 10);

            if (houseTypes.includes(intType)) {
                return ` ${quantity} ${housePlural(quantity)} ${getPropertyHouseType(intType, quantity).toLowerCase()}`;
            }
        });
    };

    const renderRoomRanges = () => (offer.stats?.rooms_ranges ? Object.keys(offer.stats?.rooms_ranges) : []);

    return (
        <DetailSectionWrapper data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_CURRENT_OFFER_SECTION}>
            <DetailViewHeader title="Aktualna oferta" />

            <ResponsiveTable>
                {offer.type === OfferType.FLAT && (
                    <ResponsiveTableRow
                        title={isApartment ? "Dostępne apartamenty:" : "Dostępne mieszkania:"}
                        text={multiToSingleLineString(`aktualnie ${propertiesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            offer.stats?.properties_count_for_sale
                        }
                                ${
                                    isApartment
                                        ? ` ${apartmentsLeftPlural(offer.stats?.properties_count_for_sale ?? 0)}`
                                        : ` ${flatsPluralize(offer.stats?.properties_count_for_sale ?? 0)}`
                                }${!isEmpty(offer.stats?.all_flat_types) ? ` w tym${renderFlatTypes()}` : ""}`)}
                    />
                )}

                {offer.type === OfferType.HOUSE ? (
                    <ResponsiveTableRow
                        title="Dostępne domy:"
                        text={`aktualnie ${housesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            !isEmpty(offer.stats?.house_types)
                                ? `${renderHouseTypes()}`
                                : `${offer.stats?.properties_count_for_sale ?? 0} ${housePlural(offer.stats?.properties_count_for_sale ?? 0)}`
                        }`}
                    />
                ) : null}

                {offer.type === OfferType.COMMERCIAL ? (
                    <ResponsiveTableRow
                        title="Dostępne lokale:"
                        text={`aktualnie ${propertiesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            offer.stats?.properties_count_for_sale ?? 0
                        } ${commercialsPlural(offer.stats?.properties_count_for_sale ?? 0)}`}
                    />
                ) : null}

                {offer.stats?.ranges_area_min ? (
                    <ResponsiveTableRow
                        title={isApartment ? "Powierzchnie apartamentów:" : `Powierzchnie ${getOfferTypeNamePluralWhat(offer.type)}:`}
                        text={`${
                            offer.stats.ranges_area_min === offer.stats.ranges_area_max
                                ? ` ${offer.stats.ranges_area_min}`
                                : `od ${offer.stats.ranges_area_min} do ${offer.stats.ranges_area_max}`
                        } m${SUP2}`}
                    />
                ) : null}

                {offer.type === OfferType.HOUSE && offer.stats?.ranges_plot_area_min ? (
                    <ResponsiveTableRow
                        title="Powierzchnie działek:"
                        text={`${
                            offer.stats.ranges_plot_area_min === offer.stats.ranges_plot_area_max
                                ? ` ${offer.stats.ranges_plot_area_min}`
                                : `od ${offer.stats.ranges_plot_area_min} do ${offer.stats.ranges_plot_area_max}`
                        } m${SUP2}`}
                    />
                ) : null}

                {offer.type === OfferType.HOUSE ||
                    (offer.type === OfferType.FLAT && (
                        <ResponsiveTableRow
                            title="Liczba pokoi:"
                            text={
                                isApartment
                                    ? `dostępne są apartamenty ${renderRoomRanges().join(", ")}-pokojowe`
                                    : `dostępne są ${getOfferTypeNamePlural(offer.type)} ${renderRoomRanges().join(", ")}-pokojowe`
                            }
                        />
                    ))}

                {showPrice && (
                    <ResponsiveTableRow
                        className={priceRowStyle}
                        title={isApartment ? `Ceny apartamentów:` : `Ceny ${getOfferTypeNamePluralWhat(offer.type)}:`}
                    >
                        {!!(
                            (offer.stats?.ranges_price_min && offer.configuration.show_price_range) ||
                            (offer.stats?.ranges_price_m2_min && offer.configuration.show_price_m2_range)
                        ) && !isAutoresponderEnabled ? (
                            <ul className={listStyles}>
                                {offer.stats?.ranges_price_min && offer.configuration.show_price_range ? (
                                    <Text as="li" variant="info_txt_1">
                                        {offer.stats.ranges_price_min === offer.stats.ranges_price_max
                                            ? `kształtują się na poziomie ${priceFormat(offer.stats.ranges_price_min, {unit: currency})}.`
                                            : `kształtują się w zakresie od ${numberFormat(offer.stats.ranges_price_min)}${
                                                  offer.stats.ranges_price_max ? ` do ${numberFormat(offer.stats.ranges_price_max)}` : ""
                                              }\xA0${currency}`}
                                    </Text>
                                ) : (
                                    ""
                                )}

                                {offer.stats?.ranges_price_m2_min && offer.configuration.show_price_m2_range ? (
                                    <Text as="li" variant="info_txt_1">
                                        {offer.stats.ranges_price_m2_min === offer.stats.ranges_price_m2_max
                                            ? `koszt metra kwadratowego wynosi ${priceM2Format(offer.stats.ranges_price_m2_min, {unit: currency})}.`
                                            : `koszt metra kwadratowego wynosi od ${numberFormat(offer.stats.ranges_price_m2_min)}${
                                                  offer.stats.ranges_price_m2_max ? ` do ${numberFormat(offer.stats.ranges_price_m2_max)}` : ""
                                              }\xA0${currency}`}
                                    </Text>
                                ) : (
                                    ""
                                )}
                            </ul>
                        ) : (
                            <Text variant="body_copy_2" as="div">
                                informacja u dewelopera
                            </Text>
                        )}
                    </ResponsiveTableRow>
                )}

                <ResponsiveTableRow title="Opis płatności:" text={offer.payment_description ? `${offer.payment_description}` : "informacja u dewelopera"} />

                <ResponsiveTableRow
                    title="Warunki rezerwacji:"
                    text={offer.reservation_description ? `${offer.reservation_description}` : "informacja u dewelopera"}
                />

                <ResponsiveTableRow
                    title="Zabezpieczenie środków klienta:"
                    text={
                        offer.funds_protection ? (
                            <OfferDetailFundsProtection
                                fundsProtection={offer.funds_protection}
                                fundsProtectionDescription={offer.funds_protection_description}
                                fundsProtectionTypes={variables != null ? variables.offer_funds_protection : []}
                            />
                        ) : (
                            "informacja u dewelopera"
                        )
                    }
                />

                <ResponsiveTableRow
                    title="Wysokość czynszu:"
                    text={
                        offer.rent_price_m2_range
                            ? `${
                                  offer.rent_price_m2_range.lower || offer.rent_price_m2_range.upper
                                      ? `planowany czynsz będzie w wysokości ${
                                            offer.rent_price_m2_range.lower === offer.rent_price_m2_range.upper
                                                ? numberFormat(offer.rent_price_m2_range.lower)
                                                : offer.rent_price_m2_range.lower
                                                  ? ` od ${numberFormat(offer.rent_price_m2_range.lower)} ${
                                                        offer.rent_price_m2_range.upper ? ` do ${numberFormat(offer.rent_price_m2_range.upper)}` : ""
                                                    }`
                                                  : ""
                                        } zł/m${SUP2}`
                                      : `${
                                            offer.rent_price_m2_range.lower === 0 && offer.rent_price_m2_range.upper === 0
                                                ? `w inwestycji znajdują się ${getOfferTypeNamePlural(offer.type)} bezczynszowe`
                                                : "informacja u dewelopera"
                                        }`
                              }`
                            : "informacja u dewelopera"
                    }
                />

                {offer.stats?.properties_count_mdm && (offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) ? (
                    <ResponsiveTableRow
                        title="Program dopłat MDM:"
                        text={
                            `${
                                offer.type === OfferType.FLAT
                                    ? `${propertiesAvailable(offer.stats.properties_count_mdm)} ${offer.stats.properties_count_mdm} ${flatsPluralize(
                                          offer.stats.properties_count_mdm
                                      )}, które `
                                    : ""
                            }` +
                            `${
                                offer.type === OfferType.HOUSE
                                    ? `${housesAvailable(offer.stats.properties_count_mdm)} ${offer.stats.properties_count_mdm} ${housePlural(
                                          offer.stats.properties_count_mdm
                                      )}, ${pluralize(["który", "które", "które"])(offer.stats.properties_count_mdm)} `
                                    : "" + `${pluralize(["spełniało", "spełniały", "spełniały"])(offer.stats.properties_count_mdm)} kryteria programu MDM 2018`
                            }`
                        }
                    />
                ) : null}

                {offer.guaranteed_return_rate ? <ResponsiveTableRow title="Gwarancja zysku:" text={`${numberFormat(offer.guaranteed_return_rate)}%`} /> : null}

                {offer.guaranteed_renting_months ? (
                    <ResponsiveTableRow
                        title="Gwarancja okresu najmu:"
                        text={`${numberFormat(offer.guaranteed_renting_months)} ${pluralize(["miesiąc", "miesiące", "miesięcy"])(
                            offer.guaranteed_renting_months
                        )}`}
                    />
                ) : null}

                {(offer.stats?.properties_with_roi_count ?? 0) > 0 ? (
                    <ResponsiveTableRow
                        title="Oferty pod inwestycje:"
                        text={
                            <>
                                W inwestycji <span className="bt">{offer.name}</span> {isNearbyPluralize(offer.stats?.properties_with_roi_count ?? 0)}{" "}
                                <span>
                                    {offer.stats?.properties_with_roi_count} {offersPlural(offer.stats?.properties_with_roi_count ?? 0)}
                                </span>{" "}
                                do zakupu pod przyszły wynajem
                                <Button variant="outlined_secondary" size="small" onClick={changeVisibility} css={mt(2)}>
                                    Zobacz oferty
                                </Button>
                            </>
                        }
                    />
                ) : null}
            </ResponsiveTable>
        </DetailSectionWrapper>
    );
};

const priceRowStyle = css({
    alignItems: "stretch"
});

const listStyles = css({
    margin: 0,
    paddingY: 0,
    paddingX: 1.5
});
import {MouseEvent} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {mt} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {multiToSingleLineString, numberFormat, pluralize, priceFormat, priceM2Format, SUP2} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {ResponsiveTable} from "../../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../../atoms/ResponsiveTableRow";
import {flatsPluralize} from "../../../real_estate/constants/flats_pluralize";
import {getCurrency} from "../../../utils/get_currency";
import {IValueLabel} from "../../../variables/types/IValueLabel";
import {DetailSectionWrapper} from "../../atoms/DetailSectionWrapper";
import {getOfferTypeNamePlural, getOfferTypeNamePluralWhat} from "../../helpers/OfferType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferType} from "../../types/OfferType";
import {PropertyFlatType} from "../../types/PropertyFlatType";
import {PropertyHouseType} from "../../types/PropertyHouseType";
import {DetailViewHeader} from "./DetailViewHeader";
import {OfferDetailFundsProtection} from "./OfferDetailFundsProtection";

interface IProps {
    changeVisibility: (e: MouseEvent) => void;
    showPrice: boolean;
    offer: IOfferDetail;
    variables: {
        offer_funds_protection: IValueLabel[];
    } | null;
}

const detachedHouse = pluralize(["wolnostojący", "wolnostojące", "wolnostojących"]);
const semiDetachedHouse = pluralize(["bliźniak", "bliźniaki", "bliźniaków"]);
const serialHouse = pluralize(["szeregowy", "szeregowe", "szeregowych"]);

function getPropertyHouseType(propertyHouseType: PropertyHouseType, quantity: number) {
    switch (propertyHouseType) {
        case PropertyHouseType.DETACHED:
            return detachedHouse(quantity);
        case PropertyHouseType.SEMI_DETACHED:
            return semiDetachedHouse(quantity);
        case PropertyHouseType.SERIAL:
            return serialHouse(quantity);
        case PropertyHouseType.WOODEN:
            return "z bali";
        default:
            throw new Error("Unknown property house type");
    }
}

function getPropertyFlatType(propertyFlatType: PropertyFlatType) {
    switch (propertyFlatType) {
        case PropertyFlatType.STANDARD:
            return "Standard";
        case PropertyFlatType.PENTHOUSE:
            return "Penthouse";
        case PropertyFlatType.APARTMENT:
            return "Apartament";
        default:
            throw new Error("Unknown property flat type");
    }
}

export const OfferDetailCurrentInfo = (props: IProps) => {
    const {offer, changeVisibility, showPrice, variables} = props;

    const isApartment = offer.is_holiday_location || offer.is_condohotel;
    const isAutoresponderEnabled = offer.configuration.is_autoresponder_enabled;

    const currency = getCurrency({currency: offer.currency});

    const localPlural = pluralize(["lokal", "lokale", "lokali"]);
    const propertiesAvailable = pluralize(["dostępny jest", "dostępne są", "dostępnych jest"]);
    const housesAvailable = pluralize(["dostępny jest", "dostępne są", "dostępnych jest"]);
    const apartmentsLeftPlural = pluralize(["apartament", "apartamenty", "apartamentów"]);
    const housePlural = pluralize(["dom", "domy", "domów"]);
    const commercialsPlural = pluralize(["lokal użytkowy", "lokale użytkowe", "lokali użytkowych"]);
    const offersPlural = pluralize(["oferta rekomendowana", "oferty rekomendowane", "ofert rekomendowanych"]);
    const isNearbyPluralize = pluralize(["znajduje się", "znajdują się", "znajduje się"]);

    const renderFlatTypes = () => {
        return Object.entries(offer.stats?.flat_types || {}).map(([type, quantity]) => {
            const intType = parseInt(type, 10);
            if (intType === PropertyFlatType.STANDARD) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            } else if (intType === PropertyFlatType.PENTHOUSE) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            } else if (intType === PropertyFlatType.APARTMENT) {
                return ` ${quantity} ${localPlural(quantity)} typu ${getPropertyFlatType(intType).toLowerCase()}`;
            }
        });
    };

    const renderHouseTypes = () => {
        const houseTypes = [PropertyHouseType.DETACHED, PropertyHouseType.SEMI_DETACHED, PropertyHouseType.SERIAL, PropertyHouseType.WOODEN];

        return Object.entries(offer.stats?.house_types || {}).map(([type, quantity]) => {
            const intType = parseInt(type, 10);

            if (houseTypes.includes(intType)) {
                return ` ${quantity} ${housePlural(quantity)} ${getPropertyHouseType(intType, quantity).toLowerCase()}`;
            }
        });
    };

    const renderRoomRanges = () => (offer.stats?.rooms_ranges ? Object.keys(offer.stats?.rooms_ranges) : []);

    return (
        <DetailSectionWrapper data-testid={OFFER_TEST_IDS.OFFER_VIEW.OFFER_CURRENT_OFFER_SECTION}>
            <DetailViewHeader title="Aktualna oferta" />

            <ResponsiveTable>
                {offer.type === OfferType.FLAT && (
                    <ResponsiveTableRow
                        title={isApartment ? "Dostępne apartamenty:" : "Dostępne mieszkania:"}
                        text={multiToSingleLineString(`aktualnie ${propertiesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            offer.stats?.properties_count_for_sale
                        }
                                ${
                                    isApartment
                                        ? ` ${apartmentsLeftPlural(offer.stats?.properties_count_for_sale ?? 0)}`
                                        : ` ${flatsPluralize(offer.stats?.properties_count_for_sale ?? 0)}`
                                }${!isEmpty(offer.stats?.all_flat_types) ? ` w tym${renderFlatTypes()}` : ""}`)}
                    />
                )}

                {offer.type === OfferType.HOUSE ? (
                    <ResponsiveTableRow
                        title="Dostępne domy:"
                        text={`aktualnie ${housesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            !isEmpty(offer.stats?.house_types)
                                ? `${renderHouseTypes()}`
                                : `${offer.stats?.properties_count_for_sale ?? 0} ${housePlural(offer.stats?.properties_count_for_sale ?? 0)}`
                        }`}
                    />
                ) : null}

                {offer.type === OfferType.COMMERCIAL ? (
                    <ResponsiveTableRow
                        title="Dostępne lokale:"
                        text={`aktualnie ${propertiesAvailable(offer.stats?.properties_count_for_sale ?? 0)} ${
                            offer.stats?.properties_count_for_sale ?? 0
                        } ${commercialsPlural(offer.stats?.properties_count_for_sale ?? 0)}`}
                    />
                ) : null}

                {offer.stats?.ranges_area_min ? (
                    <ResponsiveTableRow
                        title={isApartment ? "Powierzchnie apartamentów:" : `Powierzchnie ${getOfferTypeNamePluralWhat(offer.type)}:`}
                        text={`${
                            offer.stats.ranges_area_min === offer.stats.ranges_area_max
                                ? ` ${offer.stats.ranges_area_min}`
                                : `od ${offer.stats.ranges_area_min} do ${offer.stats.ranges_area_max}`
                        } m${SUP2}`}
                    />
                ) : null}

                {offer.type === OfferType.HOUSE && offer.stats?.ranges_plot_area_min ? (
                    <ResponsiveTableRow
                        title="Powierzchnie działek:"
                        text={`${
                            offer.stats.ranges_plot_area_min === offer.stats.ranges_plot_area_max
                                ? ` ${offer.stats.ranges_plot_area_min}`
                                : `od ${offer.stats.ranges_plot_area_min} do ${offer.stats.ranges_plot_area_max}`
                        } m${SUP2}`}
                    />
                ) : null}

                {offer.type === OfferType.HOUSE ||
                    (offer.type === OfferType.FLAT && (
                        <ResponsiveTableRow
                            title="Liczba pokoi:"
                            text={
                                isApartment
                                    ? `dostępne są apartamenty ${renderRoomRanges().join(", ")}-pokojowe`
                                    : `dostępne są ${getOfferTypeNamePlural(offer.type)} ${renderRoomRanges().join(", ")}-pokojowe`
                            }
                        />
                    ))}

                {showPrice && (
                    <ResponsiveTableRow
                        className={priceRowStyle}
                        title={isApartment ? `Ceny apartamentów:` : `Ceny ${getOfferTypeNamePluralWhat(offer.type)}:`}
                    >
                        {!!(
                            (offer.stats?.ranges_price_min && offer.configuration.show_price_range) ||
                            (offer.stats?.ranges_price_m2_min && offer.configuration.show_price_m2_range)
                        ) && !isAutoresponderEnabled ? (
                            <ul className={listStyles}>
                                {offer.stats?.ranges_price_min && offer.configuration.show_price_range ? (
                                    <Text as="li" variant="info_txt_1">
                                        {offer.stats.ranges_price_min === offer.stats.ranges_price_max
                                            ? `kształtują się na poziomie ${priceFormat(offer.stats.ranges_price_min, {unit: currency})}.`
                                            : `kształtują się w zakresie od ${numberFormat(offer.stats.ranges_price_min)}${
                                                  offer.stats.ranges_price_max ? ` do ${numberFormat(offer.stats.ranges_price_max)}` : ""
                                              }\xA0${currency}`}
                                    </Text>
                                ) : (
                                    ""
                                )}

                                {offer.stats?.ranges_price_m2_min && offer.configuration.show_price_m2_range ? (
                                    <Text as="li" variant="info_txt_1">
                                        {offer.stats.ranges_price_m2_min === offer.stats.ranges_price_m2_max
                                            ? `koszt metra kwadratowego wynosi ${priceM2Format(offer.stats.ranges_price_m2_min, {unit: currency})}.`
                                            : `koszt metra kwadratowego wynosi od ${numberFormat(offer.stats.ranges_price_m2_min)}${
                                                  offer.stats.ranges_price_m2_max ? ` do ${numberFormat(offer.stats.ranges_price_m2_max)}` : ""
                                              }\xA0${currency}`}
                                    </Text>
                                ) : (
                                    ""
                                )}
                            </ul>
                        ) : (
                            <Text variant="body_copy_2" as="div">
                                informacja u dewelopera
                            </Text>
                        )}
                    </ResponsiveTableRow>
                )}

                <ResponsiveTableRow title="Opis płatności:" text={offer.payment_description ? `${offer.payment_description}` : "informacja u dewelopera"} />

                <ResponsiveTableRow
                    title="Warunki rezerwacji:"
                    text={offer.reservation_description ? `${offer.reservation_description}` : "informacja u dewelopera"}
                />

                <ResponsiveTableRow
                    title="Zabezpieczenie środków klienta:"
                    text={
                        offer.funds_protection ? (
                            <OfferDetailFundsProtection
                                fundsProtection={offer.funds_protection}
                                fundsProtectionDescription={offer.funds_protection_description}
                                fundsProtectionTypes={variables != null ? variables.offer_funds_protection : []}
                            />
                        ) : (
                            "informacja u dewelopera"
                        )
                    }
                />

                <ResponsiveTableRow
                    title="Wysokość czynszu:"
                    text={
                        offer.rent_price_m2_range
                            ? `${
                                  offer.rent_price_m2_range.lower || offer.rent_price_m2_range.upper
                                      ? `planowany czynsz będzie w wysokości ${
                                            offer.rent_price_m2_range.lower === offer.rent_price_m2_range.upper
                                                ? numberFormat(offer.rent_price_m2_range.lower)
                                                : offer.rent_price_m2_range.lower
                                                  ? ` od ${numberFormat(offer.rent_price_m2_range.lower)} ${
                                                        offer.rent_price_m2_range.upper ? ` do ${numberFormat(offer.rent_price_m2_range.upper)}` : ""
                                                    }`
                                                  : ""
                                        } zł/m${SUP2}`
                                      : `${
                                            offer.rent_price_m2_range.lower === 0 && offer.rent_price_m2_range.upper === 0
                                                ? `w inwestycji znajdują się ${getOfferTypeNamePlural(offer.type)} bezczynszowe`
                                                : "informacja u dewelopera"
                                        }`
                              }`
                            : "informacja u dewelopera"
                    }
                />

                {offer.stats?.properties_count_mdm && (offer.type === OfferType.HOUSE || offer.type === OfferType.FLAT) ? (
                    <ResponsiveTableRow
                        title="Program dopłat MDM:"
                        text={
                            `${
                                offer.type === OfferType.FLAT
                                    ? `${propertiesAvailable(offer.stats.properties_count_mdm)} ${offer.stats.properties_count_mdm} ${flatsPluralize(
                                          offer.stats.properties_count_mdm
                                      )}, które `
                                    : ""
                            }` +
                            `${
                                offer.type === OfferType.HOUSE
                                    ? `${housesAvailable(offer.stats.properties_count_mdm)} ${offer.stats.properties_count_mdm} ${housePlural(
                                          offer.stats.properties_count_mdm
                                      )}, ${pluralize(["który", "które", "które"])(offer.stats.properties_count_mdm)} `
                                    : "" + `${pluralize(["spełniało", "spełniały", "spełniały"])(offer.stats.properties_count_mdm)} kryteria programu MDM 2018`
                            }`
                        }
                    />
                ) : null}

                {offer.guaranteed_return_rate ? <ResponsiveTableRow title="Gwarancja zysku:" text={`${numberFormat(offer.guaranteed_return_rate)}%`} /> : null}

                {offer.guaranteed_renting_months ? (
                    <ResponsiveTableRow
                        title="Gwarancja okresu najmu:"
                        text={`${numberFormat(offer.guaranteed_renting_months)} ${pluralize(["miesiąc", "miesiące", "miesięcy"])(
                            offer.guaranteed_renting_months
                        )}`}
                    />
                ) : null}

                {(offer.stats?.properties_with_roi_count ?? 0) > 0 ? (
                    <ResponsiveTableRow
                        title="Oferty pod inwestycje:"
                        text={
                            <>
                                W inwestycji <span className="bt">{offer.name}</span> {isNearbyPluralize(offer.stats?.properties_with_roi_count ?? 0)}{" "}
                                <span>
                                    {offer.stats?.properties_with_roi_count} {offersPlural(offer.stats?.properties_with_roi_count ?? 0)}
                                </span>{" "}
                                do zakupu pod przyszły wynajem
                                <Button variant="outlined_secondary" size="small" onClick={changeVisibility} css={mt(2)}>
                                    Zobacz oferty
                                </Button>
                            </>
                        }
                    />
                ) : null}
            </ResponsiveTable>
        </DetailSectionWrapper>
    );
};

const priceRowStyle = css({
    alignItems: "stretch"
});

const listStyles = css({
    margin: 0,
    paddingY: 0,
    paddingX: 1.5
});
